import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BookmarkCheck,
  ListChecks,
  Loader2,
  Menu as MenuIcon,
  ShoppingBasket,
  Sparkles,
  UserRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePremiumGate } from "@/hooks/usePremiumGate";

/**
 * Header nav reused across the app entry pages: My recipes, Menus,
 * Shopping list, then a bold "Clean another recipe" CTA. On mobile the
 * nav collapses into a hamburger sheet so the header stays a single row.
 */
type Props = {
  className?: string;
  /**
   * Where the "Clean another recipe" CTA should send the user. Defaults
   * to "/?capture=1" — a stripped-down capture screen for returning users
   * that hides the marketing landing sections.
   */
  cleanAnotherHref?: string;
  /** Optional click handler for the CTA — overrides href navigation. */
  onCleanAnotherClick?: () => void;
  /** Hide the CTA on the capture page itself (already there). */
  hideCleanAnother?: boolean;
};

type MenuLite = { id: string; title: string };

export const MyRecipesLink = ({
  className,
  cleanAnotherHref = "/?capture=1",
  onCleanAnotherClick,
  hideCleanAnother,
}: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [resolving, setResolving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menusWithLists, setMenusWithLists] = useState<MenuLite[]>([]);
  // Tri-state so we don't flicker the Account link while the session is
  // still resolving (e.g. token refresh, recovery link, cross-tab sync).
  const [authState, setAuthState] = useState<"loading" | "in" | "out">("loading");
  const [sheetOpen, setSheetOpen] = useState(false);

  // Auto-close the mobile sheet on any route change so navigating from inside
  // it always dismisses the overlay — including for premium-gate redirects
  // (e.g. /upgrade, /my-recipes sign-in) that happen asynchronously.
  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname, location.search]);

  // Track auth so the Account link routes correctly. We listen to all auth
  // changes (sign-in, sign-out, token refresh, user updates) and re-check
  // on tab refocus so cross-tab sign-in/out stays in sync.
  useEffect(() => {
    let mounted = true;
    const sync = () =>
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (mounted) setAuthState(session?.user ? "in" : "out");
      });
    sync();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!mounted) return;
      setAuthState(session?.user ? "in" : "out");
      // Dismiss the mobile sheet on any auth transition (sign-in, sign-out,
      // token refresh after a recovery link) so it never lingers over a
      // freshly authenticated/cleared UI.
      setSheetOpen(false);
    });
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const menusGate = usePremiumGate({
    variant: "menus",
    returnUrl: "/menus",
    onAllowed: () => navigate("/menus"),
  });

  const resolveShoppingList = async () => {
    if (resolving) return;
    setResolving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.email) {
        navigate("/my-recipes");
        return;
      }
      const { data, error } = await supabase
        .from("menus")
        .select("id, title, shopping_lists!inner(id)")
        .order("updated_at", { ascending: false });
      if (error) {
        console.error(error);
        toast.error("Couldn't load your shopping lists");
        return;
      }
      const lists = (data || []).map((m: { id: string; title: string }) => ({
        id: m.id,
        title: m.title,
      }));
      if (lists.length === 0) {
        toast.message("No shopping lists yet", {
          description: "Open a menu & tap Create Shopping List to start.",
        });
        navigate("/menus");
        return;
      }
      if (lists.length === 1) {
        navigate(`/menus/${lists[0].id}/list`);
        return;
      }
      setMenusWithLists(lists);
      setPickerOpen(true);
    } finally {
      setResolving(false);
    }
  };

  const shoppingGate = usePremiumGate({
    variant: "menus",
    returnUrl: "/menus",
    onAllowed: () => {
      void resolveShoppingList();
    },
  });

  const handleShoppingList = (e: React.MouseEvent) => {
    e.preventDefault();
    shoppingGate.trigger();
  };

  // Active-route detection. We highlight whichever section the user is
  // currently inside so the nav doubles as a "you are here" indicator on
  // both desktop and inside the mobile sheet.
  const path = location.pathname;
  const isActive = {
    myRecipes: path === "/my-recipes",
    shopping: path.startsWith("/menus/") && path.endsWith("/list"),
    menus: path === "/menus" || (path.startsWith("/menus/") && !path.endsWith("/list")),
    account: path === "/account",
  };

  // Shared item renderer — used in both desktop row and mobile sheet.
  // `mobile` switches to a tappable, full-width row inside the sheet.
  const renderItems = (mobile: boolean) => {
    const closeSheet = () => mobile && setSheetOpen(false);
    const baseItem = mobile
      ? "w-full justify-start h-11 text-base"
      : className;
    // Highlight uses semantic tokens so it adapts to light/dark themes.
    const activeStyle =
      "bg-primary/10 text-primary font-semibold hover:bg-primary/15 hover:text-primary";

    return (
      <>
        <Button
          variant="ghost"
          size={mobile ? "default" : "sm"}
          asChild
          className={cn(baseItem, isActive.myRecipes && activeStyle)}
          aria-current={isActive.myRecipes ? "page" : undefined}
        >
          <Link to="/my-recipes" onClick={closeSheet}>
            <BookmarkCheck className="mr-2 h-4 w-4" />
            My recipes
          </Link>
        </Button>
        <Button
          variant="ghost"
          size={mobile ? "default" : "sm"}
          onClick={(e) => {
            e.preventDefault();
            closeSheet();
            menusGate.trigger();
          }}
          className={cn(baseItem, isActive.menus && activeStyle)}
          aria-current={isActive.menus ? "page" : undefined}
        >
          <ListChecks className="mr-2 h-4 w-4" />
          Menus
        </Button>
        <Button
          variant="ghost"
          size={mobile ? "default" : "sm"}
          onClick={(e) => {
            closeSheet();
            handleShoppingList(e);
          }}
          disabled={resolving}
          className={cn(baseItem, isActive.shopping && activeStyle)}
          aria-current={isActive.shopping ? "page" : undefined}
        >
          {resolving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <ShoppingBasket className="mr-2 h-4 w-4" />
          )}
          Shopping list
        </Button>
        {/* Always render Account so the nav doesn't shift while auth resolves.
            Signed-out users are routed to /my-recipes which hosts the sign-in
            flow; the redirect bounces them to /account after they sign in. */}
        <Button
          variant="ghost"
          size={mobile ? "default" : "sm"}
          asChild
          className={cn(baseItem, isActive.account && activeStyle)}
          aria-current={isActive.account ? "page" : undefined}
        >
          <Link
            to={authState === "in" ? "/account" : "/my-recipes"}
            aria-label="Account"
            onClick={closeSheet}
          >
            <UserRound className="mr-2 h-4 w-4" />
            Account
          </Link>
        </Button>
        {!hideCleanAnother &&
          (onCleanAnotherClick ? (
            <Button
              size={mobile ? "default" : "sm"}
              onClick={() => {
                closeSheet();
                onCleanAnotherClick();
              }}
              className={cn(
                mobile
                  ? "w-full justify-start h-11 text-base mt-2"
                  : "ml-1 rounded-full px-4",
                "bg-primary font-semibold text-primary-foreground shadow-soft hover:bg-primary-hover",
                !mobile && "rounded-full",
              )}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Clean another
            </Button>
          ) : (
            <Button
              size={mobile ? "default" : "sm"}
              asChild
              className={cn(
                mobile
                  ? "w-full justify-start h-11 text-base mt-2"
                  : "ml-1 rounded-full px-4",
                "bg-primary font-semibold text-primary-foreground shadow-soft hover:bg-primary-hover",
              )}
            >
              <Link to={cleanAnotherHref} onClick={closeSheet}>
                <Sparkles className="mr-2 h-4 w-4" />
                Clean another
              </Link>
            </Button>
          ))}
      </>
    );
  };

  return (
    <>
      {/* Desktop / tablet: inline row */}
      <div className="hidden sm:flex flex-wrap items-center justify-end gap-1">
        {renderItems(false)}
      </div>

      {/* Mobile: hamburger sheet */}
      <div className="sm:hidden">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="Open menu">
              <MenuIcon className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 p-0">
            <SheetHeader className="px-6 pt-6 pb-2">
              <SheetTitle>Menu</SheetTitle>
            </SheetHeader>
            {/* Delegated click handler: close the sheet on ANY interactive
                tap inside the nav, including same-route taps that wouldn't
                otherwise trigger the location-change effect (e.g. tapping
                "My recipes" while already on /my-recipes, or a premium gate
                that resolves to the current page). */}
            <nav
              className="flex flex-col gap-1 px-4 pb-6"
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("a,button")) {
                  setSheetOpen(false);
                }
              }}
            >
              {renderItems(true)}
            </nav>
          </SheetContent>
        </Sheet>
      </div>

      {/* Shopping-list picker (shared) */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="sr-only" aria-hidden="true" tabIndex={-1}>
            Open shopping list picker
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-64 p-2">
          <p className="px-2 pb-2 pt-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Open shopping list
          </p>
          <ul className="flex flex-col">
            {menusWithLists.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => {
                    setPickerOpen(false);
                    navigate(`/menus/${m.id}/list`);
                  }}
                  className="w-full truncate rounded-md px-2 py-2 text-left text-sm transition-smooth hover:bg-muted"
                >
                  {m.title}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </>
  );
};
